import { useContext, useEffect, useState } from 'react';
import { Button, Form, Image, Modal } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../api/axiosInstance';
import icSuccess from '../../assets/img/ic-celebration-circle.svg';
import { UserContext } from '../../UserContext';
import ProfileUpdateSuccessModal from '../Profile/ProfileUpdateSuccessModal';
import { trackEvent } from '../../services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from '../../services/analytics/analyticsEvents';
import { isProfileComplete, shouldShowProfileReminder } from '../../utils/userCompletion';
import {
    dismissIncompleteProfileReminderForSession,
    getInitialOnboardingStep,
    getOnboardingStepName,
    INCOMPLETE_PROFILE_REMINDER_SESSION_KEY,
} from '../../utils/onboarding';

const IncompleteProfileReminder = () => {
    const { user, setUser } = useContext(UserContext) || {};
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useMediaQuery({ query: '(max-width: 767.98px)' });
    const [show, setShow] = useState(false);
    const [dontRemindAgain, setDontRemindAgain] = useState(false);

    const step = getInitialOnboardingStep(user);
    const isOnboarding = location.pathname === '/onboarding' || location.pathname.startsWith('/onboarding/');
    const isAuthenticationRoute = [
        '/',
        '/magic-login',
        '/register',
        '/password-reset',
        '/password-reset-token',
    ].some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));

    useEffect(() => {
        const isIncompleteStudent =
            Boolean(user?._id) &&
            String(user?.role || '').trim().toLowerCase() === 'student' &&
            !isProfileComplete(user);

        if (isIncompleteStudent && isOnboarding) {
            dismissIncompleteProfileReminderForSession();
            setShow(false);
            return;
        }

        const dismissedThisSession =
            sessionStorage.getItem(INCOMPLETE_PROFILE_REMINDER_SESSION_KEY) === 'true';
        const shouldShow =
            shouldShowProfileReminder(user, dismissedThisSession) &&
            !isOnboarding &&
            !isAuthenticationRoute;

        setShow(shouldShow);
    }, [isAuthenticationRoute, isOnboarding, user]);

    const dismissForSession = () => {
        dismissIncompleteProfileReminderForSession();
        setShow(false);
    };

    const handleCompleteProfile = () => {
        dismissForSession();
        navigate('/onboarding');
    };

    const handleReminderDismiss = async () => {
        try {
            if (dontRemindAgain) {
                const res = await api.patch('/api/auth/users/me/profile-reminder', {
                    suppressProfileReminder: true,
                });

                localStorage.setItem('user', JSON.stringify(res.data.user));
                if (typeof setUser === 'function') setUser(res.data.user);
            }
        } catch (err) {
            console.error('Failed to save reminder preference:', err);
        } finally {
            dismissForSession();
            setDontRemindAgain(false);
            trackEvent(ANALYTICS_EVENTS.ONBOARDING_MAYBE_LATER_CLICKED, {
                step,
                stepName: getOnboardingStepName(step),
                destination: '/discovery/companies',
                source: 'onboarding_reminder_modal',
            });

            navigate('/discovery/companies', { replace: true });
        }
    };

    const title = step === 1
        ? 'Complete your profile'
        : (
            <>
                Complete your profile
                <br />
                Step {step} of 3
            </>
        );
    const message = step === 1
        ? 'Your profile is still incomplete. Complete your details to improve your experience on aBROADIFY.'
        : 'You already completed part of your profile. Continue to improve your experience on aBROADIFY.';
    const primaryLabel = step === 1 ? 'Complete profile' : `Continue to step ${step}`;
    const checkbox = (
        <Form.Group className="mt-4 justify-content-center d-flex">
            <Form.Check
                type="checkbox"
                id="dont-remind-again"
                label="Don't remind me again"
                checked={dontRemindAgain}
                onChange={(event) => setDontRemindAgain(event.target.checked)}
            />
        </Form.Group>
    );

    if (isMobile) return (
        <ProfileUpdateSuccessModal
            show={show}
            onHide={handleReminderDismiss}
            backdrop="static"
            title={title}
            message={message}
            icon={icSuccess}
            bodyContent={checkbox}
            actions={(
                <>
                    <Button variant="primary" onClick={handleCompleteProfile}>
                        {primaryLabel}
                    </Button>
                    <Button variant="outline-primary" onClick={handleReminderDismiss}>
                        Maybe later
                    </Button>
                </>
            )}
        />
    );

    return (
        <Modal show={show} onHide={handleReminderDismiss} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title></Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Image src={icSuccess} alt="" className="d-block m-auto mb-4" />
                <h2 className="text-center mb-3">{title}</h2>
                <p className="text-center">{message}</p>
                {checkbox}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-center flex-column">
                <Button variant="primary" onClick={handleCompleteProfile} className="mb-3">
                    {primaryLabel}
                </Button>
                <Button variant="outline-primary" onClick={handleReminderDismiss}>
                    Maybe later
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default IncompleteProfileReminder;
